import { manifestLoader } from './manifest-loader';
import { MseVideoPlayer } from './mse-video-player';
import type { PlaylistItem, TransitionConfig, PlayEvent, ManifestAsset } from '../types';

export type PlayEventCallback = (event: PlayEvent) => void;

/**
 * Single-element video renderer with canvas mask.
 *
 * Background: prior dual-slot attempts kept a second <video> element loading
 * in parallel with the active one, then called play() on it to "prime" the
 * decoder. That forces TWO simultaneously active H.264 decoders, which:
 *   - Crashes/stalls on webOS Signage TVs (single hardware decoder)
 *   - Stutters on Chrome (the OS-level codec scheduler doesn't expect
 *     parallel streams from the same tab and degrades both)
 *
 * The user confirmed the FIRST video always plays fine. The first video uses
 * the simplest possible path: one element, set src, wait canplay, play. The
 * subsequent items were broken because we deviated from that path. This
 * version uses the SAME single-element path for every video. To hide the
 * brief canplay wait at swap time (during which the <video> element shows
 * whatever frame was last composited — i.e. the outgoing video's last
 * frame), we paint that last frame to a 2D canvas overlay BEFORE changing
 * src, then drop the overlay once the new video has produced its first
 * frame. Visually identical to what the user saw before, except the wait
 * happens behind a stable canvas instead of a juddery <video>.
 *
 * Image items use a separate <img> layer that overlays the video — toggling
 * its opacity is essentially free and doesn't touch the video pipeline.
 */
class ContentRenderer {
  private container: HTMLElement | null = null;

  // The single persistent video element. Created in initialize() and reused
  // for every video item. When MSE is supported, this same element is
  // re-parented to the MseVideoPlayer at first-video-encountered.
  private video: HTMLVideoElement | null = null;

  // MSE-based seamless playback engine. Lazily constructed at the first
  // fragmented-MP4 video item we encounter. While this is non-null the
  // legacy `video` element is the one OWNED by the MseVideoPlayer (we
  // don't keep a separate one in the container). Falls back to the legacy
  // canvas-mask path when MSE is unavailable or the asset isn't a
  // fragmented MP4 (returns false from appendItem).
  private mseVideoPlayer: MseVideoPlayer | null = null;
  private mseUnsupported = false;

  // The persistent image layer above the video element.
  private imageLayer: HTMLImageElement | null = null;

  private currentAssetId: string | null = null;
  private currentAssetType: 'image' | 'video' | null = null;
  private currentItem: PlaylistItem | null = null;
  private playStartTime: number = 0;
  private onPlayEvent: PlayEventCallback | null = null;

  // Serialize concurrent renderAsset calls so two items can't race on the
  // single <video>'s src setter.
  private renderQueue: Promise<void> = Promise.resolve();

  initialize(containerId: string): void {
    this.container = document.getElementById(containerId);
    if (!this.container) {
      throw new Error(`Container element not found: ${containerId}`);
    }

    this.container.innerHTML = '';
    this.container.style.overflow = 'hidden';

    // Persistent <video>.
    const v = document.createElement('video');
    v.muted = true;
    v.playsInline = true;
    v.preload = 'auto';
    v.loop = false;
    v.autoplay = false;
    v.style.cssText = `
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: contain;
      opacity: 0;
      z-index: 1;
    `;
    v.onerror = () => console.error('[Renderer] video element error');
    v.addEventListener('ended', () => {
      v.dispatchEvent(new CustomEvent('playback-complete'));
    });
    this.container.appendChild(v);
    this.video = v;

    // Persistent <img>. Sits above the video so showing an image naturally
    // covers whatever the video was last showing.
    const img = document.createElement('img');
    img.alt = '';
    img.draggable = false;
    img.style.cssText = `
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: contain;
      opacity: 0;
      z-index: 5;
      pointer-events: none;
    `;
    this.container.appendChild(img);
    this.imageLayer = img;

    console.log('[Renderer] Initialized single-video + image layer');
  }

  setPlayEventCallback(callback: PlayEventCallback): void {
    this.onPlayEvent = callback;
  }

  async renderAsset(asset: ManifestAsset, playlistId: string, fitMode: 'FIT' | 'FILL' = 'FIT'): Promise<void> {
    const next = this.renderQueue.then(() => this.renderAssetInner(asset, playlistId, fitMode));
    this.renderQueue = next.catch(() => undefined);
    return next;
  }

  private async renderAssetInner(asset: ManifestAsset, playlistId: string, fitMode: 'FIT' | 'FILL'): Promise<void> {
    if (!this.container || !this.video || !this.imageLayer) {
      throw new Error('Renderer not initialized');
    }

    const objectFit = fitMode === 'FILL' ? 'cover' : 'contain';
    console.log(`[Renderer] renderAsset start id=${asset.id} type=${asset.type} fit=${fitMode}`);

    if (this.currentAssetId && this.onPlayEvent) {
      this.onPlayEvent({
        assetId: this.currentAssetId,
        playlistId,
        startedAt: new Date(this.playStartTime).toISOString(),
        endedAt: new Date().toISOString(),
        durationMs: Date.now() - this.playStartTime,
        completed: true,
      });
    }

    if (asset.type === 'video') {
      await this.renderVideo(asset, objectFit);
    } else if (asset.type === 'image') {
      await this.renderImage(asset, objectFit);
    } else {
      console.warn(`[Renderer] Unknown asset type: ${asset.type}`);
    }

    this.currentAssetId = asset.id;
    this.currentAssetType = asset.type === 'video' || asset.type === 'image' ? asset.type : null;
    this.playStartTime = Date.now();
    console.log(`[Renderer] renderAsset done id=${asset.id}`);
  }

  // ---------------------------------------------------------------------------
  // Image path
  // ---------------------------------------------------------------------------

  private async renderImage(asset: ManifestAsset, objectFit: string): Promise<void> {
    if (!this.imageLayer || !this.video) return;

    // Hide the video element FIRST — literally before any other operation,
    // including the IndexedDB-backed getAssetUrl lookup. Any await here
    // leaves a window in which the user sees the previous video's last
    // frame through the image's transparent letterbox bars (object-fit:
    // contain creates transparent areas on aspect-ratio mismatch).
    // The MSE path re-shows opacity at line ~218; the legacy renderVideo
    // path re-shows at line ~294. The image layer also has a black
    // background to belt-and-suspenders the bars themselves.
    try {
      this.video.pause();
      this.video.style.opacity = '0';
    } catch { /* ignore */ }

    const url = (await manifestLoader.getAssetUrl(asset.id)) || asset.url;
    if (!url) return;

    this.imageLayer.src = url;
    this.imageLayer.style.objectFit = objectFit;
    this.imageLayer.style.backgroundColor = '#000';
    try {
      await this.imageLayer.decode();
    } catch { /* paint anyway */ }
    this.imageLayer.style.opacity = '1';
  }

  // ---------------------------------------------------------------------------
  // Video path: single element + canvas mask
  // ---------------------------------------------------------------------------

  private async renderVideo(asset: ManifestAsset, objectFit: string): Promise<void> {
    if (!this.video || !this.imageLayer || !this.container) return;

    // MSE fast path. The first time we encounter a video item, try to
    // bootstrap the MseVideoPlayer. If MediaSource isn't supported or
    // the asset isn't a fragmented MP4, we fall through to the legacy
    // canvas-mask path below — which still works, just with the
    // src-swap freeze the user reported.
    if (!this.mseUnsupported) {
      if (!this.mseVideoPlayer) {
        if (MseVideoPlayer.isSupported()) {
          this.mseVideoPlayer = new MseVideoPlayer();
          // Remove the legacy <video> and let the MseVideoPlayer create
          // its own (which it owns; we don't keep a separate handle).
          try { this.video.remove(); } catch { /* ignore */ }
          this.video = this.mseVideoPlayer.attach(this.container, { opacity: '1', zIndex: '1' });
        } else {
          console.warn('[Renderer] MSE not supported — falling back to legacy single-element path');
          this.mseUnsupported = true;
        }
      }
      if (this.mseVideoPlayer) {
        const mseVideo = this.mseVideoPlayer.getVideoElement();
        if (mseVideo) {
          mseVideo.style.objectFit = objectFit;
          const appended = await this.mseVideoPlayer.appendItem(asset).catch((e) => {
            console.warn('[Renderer] MseVideoPlayer.appendItem threw:', e);
            return false;
          });
          if (appended) {
            // Hide the image layer (if anything was showing there before),
            // and ensure the video is playing. The MseVideoPlayer's
            // <video> is autoplay=true so it kicks off on first append.
            this.imageLayer.style.opacity = '0';
            // Re-show the MSE video element. The legacy path re-shows at
            // line ~291; MSE path needs the same guarantee in case the
            // previous renderImage() hid it via opacity:0 (see renderImage).
            mseVideo.style.opacity = '1';
            if (mseVideo.paused) {
              mseVideo.play().catch((e) => console.warn('[Renderer] mse video play() rejected:', e));
            }
            return;
          }
          // appendItem returned false: asset isn't fragmented, or another
          // soft failure. Fall through to canvas-mask path for THIS item.
        }
      }
    }

    const url = (await manifestLoader.getAssetUrl(asset.id)) || asset.url;
    if (!url) return;

    const video = this.video;
    const isFirstVideo = !video.src || video.readyState === 0;
    // Detect whether the LAST thing shown was an image (i.e. the image
    // layer is currently visible on top of the video element). If it
    // was, we DO NOT want to snapshot the video element's surface —
    // that surface still holds the previous video's last decoded frame
    // and would briefly flash on top of the image as the new src loads.
    // Instead we let the image layer itself act as the visual mask
    // while the new video loads, then hide image + show video together.
    const imageWasVisible = this.imageLayer.style.opacity === '1';

    // For the very first video (or after a long idle), no prior frame to
    // hide behind — just go straight to set src + canplay + play. Same
    // path the user confirmed works without freeze.
    if (isFirstVideo) {
      video.src = url;
      video.style.objectFit = objectFit;
      video.load();
      await this.waitForCanplay(video);
      this.imageLayer.style.opacity = '0';
      video.style.opacity = '1';
      try { await video.play(); } catch (e) { console.warn('[Renderer] first play() rejected:', e); }
      return;
    }

    // Subsequent transitions: if a video was the last thing visible,
    // snapshot the current frame to a canvas overlay so the user sees a
    // stable still during the src-change decoder reset (otherwise the
    // <video> surface flashes garbage mid-decode). If an IMAGE was the
    // last thing visible, do NOT add a canvas — the image layer itself
    // is a perfectly stable visual mask. Adding a canvas snapshot of
    // the video element here would actually MAKE THINGS WORSE: it would
    // capture the previous video's last decoded frame (stale) and
    // overlay it ON TOP of the image, which is exactly the "weird flash
    // of the old video right after the image ends" the user reported.
    let overlay: HTMLCanvasElement | null = null;
    if (!imageWasVisible) {
      overlay = this.captureFrameToCanvas(video);
      if (overlay) this.container.appendChild(overlay);
    }

    try { video.pause(); } catch { /* ignore */ }
    video.src = url;
    video.style.objectFit = objectFit;
    video.load();

    await this.waitForCanplay(video);

    // play() in Chrome resolves once playback STATE is "playing", not when
    // the first frame is composited. Use requestVideoFrameCallback (when
    // available) to wait for an actual rendered frame before dropping
    // the canvas overlay — that way the cut is between the canvas image
    // and a moving frame, not between the canvas and a brief blank state.
    try {
      await video.play();
    } catch (e) {
      console.warn('[Renderer] swap play() rejected:', e);
    }
    await this.waitForFirstRenderedFrame(video);

    this.imageLayer.style.opacity = '0';
    video.style.opacity = '1';
    if (overlay) {
      try { overlay.remove(); } catch { /* ignore */ }
    }
  }

  private async waitForCanplay(video: HTMLVideoElement): Promise<void> {
    if (video.readyState >= 3) return;
    await new Promise<void>((resolve) => {
      let done = false;
      const settle = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        video.removeEventListener('canplay', settle);
        video.removeEventListener('loadeddata', settle);
        video.removeEventListener('error', settle);
      };
      const settleAndResolve = () => { settle(); resolve(); };
      const timer = setTimeout(settleAndResolve, 5000);
      video.addEventListener('canplay', settleAndResolve, { once: true });
      video.addEventListener('loadeddata', settleAndResolve, { once: true });
      video.addEventListener('error', settleAndResolve, { once: true });
    });
  }

  /**
   * Wait until at least one frame has been actually rendered to the
   * video element's surface. Uses requestVideoFrameCallback where
   * supported (Chrome, webOS modern Chromium). Falls back to a small
   * fixed timeout on Safari / older browsers.
   */
  private async waitForFirstRenderedFrame(video: HTMLVideoElement): Promise<void> {
    const rvfc = (video as unknown as { requestVideoFrameCallback?: (cb: () => void) => number }).requestVideoFrameCallback;
    if (typeof rvfc === 'function') {
      await new Promise<void>((resolve) => {
        let done = false;
        const fin = () => { if (!done) { done = true; clearTimeout(t); resolve(); } };
        const t = setTimeout(fin, 1500);
        rvfc.call(video, fin);
      });
    } else {
      await new Promise<void>((r) => setTimeout(r, 80));
    }
  }

  private captureFrameToCanvas(video: HTMLVideoElement): HTMLCanvasElement | null {
    if (!video.videoWidth || !video.videoHeight) return null;
    const c = document.createElement('canvas');
    c.width = video.videoWidth;
    c.height = video.videoHeight;
    c.style.cssText = `
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: ${video.style.objectFit || 'contain'};
      z-index: 10;
      pointer-events: none;
    `;
    try {
      c.getContext('2d')!.drawImage(video, 0, 0, c.width, c.height);
    } catch (e) {
      console.warn('[Renderer] canvas snapshot failed:', e);
      return null;
    }
    return c;
  }

  /**
   * No-op for videos in this renderer — there's no off-screen slot to
   * preload into. Kept as a public method for backwards compatibility
   * with player.ts which still calls it.
   */
  async prepareAsset(_asset: ManifestAsset, _fitMode: 'FIT' | 'FILL' = 'FIT'): Promise<void> {
    // intentionally empty
  }

  /**
   * Public for player.ts; same no-op as prepareAsset.
   */
  async primeNextVideoSlot(_assetId: string, _url: string): Promise<void> {
    // intentionally empty
  }

  // ---------------------------------------------------------------------------
  // Legacy / compatibility surface
  // ---------------------------------------------------------------------------

  async render(item: PlaylistItem, playlistId: string, _transition?: TransitionConfig): Promise<void> {
    const inferred: ManifestAsset = {
      id: item.assetId,
      type: item.assetType === 'VIDEO' ? 'video' : 'image',
      url: item.url,
    } as ManifestAsset;
    await this.renderAsset(inferred, playlistId, 'FIT');
    this.currentItem = item;
  }

  getCurrentItem(): PlaylistItem | null {
    return this.currentItem;
  }

  enableVideoLoop(loop: boolean): void {
    if (!this.video) return;
    this.video.loop = loop;
  }

  clear(): void {
    if (this.mseVideoPlayer) {
      this.mseVideoPlayer.reset().catch(() => null);
    } else if (this.video) {
      try {
        this.video.pause();
        this.video.src = '';
        this.video.load();
      } catch { /* ignore */ }
      this.video.style.opacity = '0';
    }
    if (this.imageLayer) {
      try {
        this.imageLayer.src = '';
        this.imageLayer.style.opacity = '0';
      } catch { /* ignore */ }
    }
    this.currentAssetId = null;
    this.currentAssetType = null;
    this.currentItem = null;
  }

  destroy(): void {
    this.clear();
    if (this.mseVideoPlayer) {
      this.mseVideoPlayer.detach();
      this.mseVideoPlayer = null;
    }
    if (this.container) {
      this.container.innerHTML = '';
    }
    this.container = null;
    this.video = null;
    this.imageLayer = null;
    this.onPlayEvent = null;
  }

  async waitForCompletion(): Promise<void> {
    if (!this.video || this.currentAssetType !== 'video') return;
    const v = this.video;
    if (v.ended) return;
    if (v.paused) {
      v.play().catch(() => null);
    }
    return new Promise((resolve) => {
      const cleanup = () => {
        v.removeEventListener('ended', onEnded);
        v.removeEventListener('error', onError);
      };
      const onEnded = () => { cleanup(); resolve(); };
      const onError = () => { cleanup(); resolve(); };
      v.addEventListener('ended', onEnded);
      v.addEventListener('error', onError);
    });
  }
}

export const renderer = new ContentRenderer();
